/**
 * PondConfirmation Component
 * Review/confirmation page showing all answered questions and entered data for a pond
 * Allows editing individual sections before confirming and moving to next pond
 */

import React from "react";
import LabeledImageUpload from "./LabeledImageUpload";

const PondConfirmation = ({
  currentData,
  onEdit,
  onConfirm,
  onBack,
  canGoBack = false,
  isCompleted = false,
  onDataUpdate,
  projectId,
  clientId,
}) => {
  // Debug: Log current data to see what's being passed
  console.log("PondConfirmation - currentData:", currentData);
  console.log("PondConfirmation - Grading Plan Data:", {
    has_grading_plan_info: currentData?.has_grading_plan_info,
    grading_plan_sheet_number: currentData?.grading_plan_sheet_number,
    grading_plan_bottom_callout: currentData?.grading_plan_bottom_callout,
    grading_plan_top_berm_elevation:
      currentData?.grading_plan_top_berm_elevation,
    grading_plan_spillway_elevation:
      currentData?.grading_plan_spillway_elevation,
    grading_plan_lowest_contour: currentData?.grading_plan_lowest_contour,
    grading_plan_images: currentData?.grading_plan_images,
  });

  const renderYesNoAnswer = (label, value, editKey) => {
    if (value === undefined || value === null) return null;

    return (
      <div style={{ marginBottom: "15px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span
              style={{
                fontWeight: "bold",
                fontSize: "15px",
                color: "var(--text-primary)",
              }}
            >
              {label}
            </span>
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                disabled
                style={{
                  padding: "8px 25px",
                  fontSize: "14px",
                  border: "1px solid var(--border-primary)",
                  backgroundColor:
                    value === true
                      ? "var(--surface-hover)"
                      : "var(--surface-secondary)",
                  cursor: "not-allowed",
                  fontWeight: value === true ? "bold" : "normal",
                  color: "var(--text-primary)",
                }}
              >
                Yes
              </button>
              <button
                disabled
                style={{
                  padding: "8px 25px",
                  fontSize: "14px",
                  border: "1px solid var(--border-primary)",
                  backgroundColor:
                    value === false
                      ? "var(--surface-hover)"
                      : "var(--surface-secondary)",
                  cursor: "not-allowed",
                  fontWeight: value === false ? "bold" : "normal",
                  color: "var(--text-primary)",
                }}
              >
                No
              </button>
            </div>
          </div>
          <button
            onClick={() => onEdit(editKey)}
            style={{
              padding: "6px 20px",
              fontSize: "14px",
              border: "1px solid var(--border-primary)",
              backgroundColor: "var(--surface-secondary)",
              color: "var(--text-primary)",
              cursor: "pointer",
            }}
          >
            Edit
          </button>
        </div>
      </div>
    );
  };

  const renderDataSection = (title, data, editKey) => {
    // Filter out null/undefined/empty values from the data object
    const filteredData = {};
    Object.entries(data).forEach(([key, value]) => {
      // Skip N/A flag fields
      if (key.includes("_na")) return;

      // Skip fields where corresponding _na field is true
      const naFieldName = `${key}_na`;
      if (currentData[naFieldName] === true) return;

      // Skip fields with 'N/A' value
      if (value === "N/A") return;

      // Skip null, undefined, or empty string values
      if (value === null || value === undefined || value === "") return;

      // Skip empty arrays
      if (Array.isArray(value) && value.length === 0) return;

      // Include the value
      filteredData[key] = value;
    });

    // Debug logging
    console.log(`renderDataSection - ${title}:`, {
      rawData: data,
      filteredData: filteredData,
      filteredCount: Object.keys(filteredData).length,
    });

    if (Object.keys(filteredData).length === 0) return null;

    return (
      <div
        style={{
          marginBottom: "25px",
          padding: "15px",
          backgroundColor: "var(--surface-tertiary)",
          color: "var(--text-primary)",
          border: "1px solid var(--border-secondary)",
          borderRadius: "4px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "15px",
          }}
        >
          <h4
            style={{
              margin: 0,
              fontSize: "16px",
              color: "var(--text-primary)",
            }}
          >
            {title}
          </h4>
          <button
            onClick={() => onEdit(editKey)}
            style={{
              padding: "6px 20px",
              fontSize: "14px",
              border: "1px solid var(--border-primary)",
              backgroundColor: "var(--surface-secondary)",
              color: "var(--text-primary)",
              cursor: "pointer",
            }}
          >
            Edit
          </button>
        </div>

        <div style={{ display: "grid", gap: "10px" }}>
          {Object.entries(filteredData).map(([key, value]) => {
            // Format the key name
            const formattedKey = key
              .replace(/_/g, " ")
              .replace(/\b\w/g, (char) => char.toUpperCase());

            // Handle different value types
            let displayValue = value;
            let isImageField = false;

            if (Array.isArray(value)) {
              isImageField = key.includes("image");
              if (!isImageField) {
                displayValue = `${value.length} item(s)`;
              }
            } else if (typeof value === "boolean") {
              displayValue = value ? "Yes" : "No";
            }

            // Render editable image upload component
            if (isImageField) {
              return (
                <div key={key} style={{ marginBottom: "15px" }}>
                  <span
                    style={{
                      fontWeight: "500",
                      display: "block",
                      marginBottom: "8px",
                      color: "var(--text-primary)",
                    }}
                  >
                    {formattedKey}:
                  </span>
                  <LabeledImageUpload
                    images={value || []}
                    onImagesChange={(updatedImages) => {
                      if (onDataUpdate) {
                        onDataUpdate(key, updatedImages);
                      }
                    }}
                    projectId={projectId}
                    clientId={clientId}
                    hideUploadBox={true}
                  />
                </div>
              );
            }

            return (
              <div key={key} style={{ display: "flex", gap: "10px" }}>
                <span style={{ fontWeight: "500", minWidth: "200px" }}>
                  {formattedKey}:
                </span>
                <span>{displayValue}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="pond-confirmation" style={{ padding: "20px" }}>
      <div
        style={{
          backgroundColor: "var(--surface-secondary)",
          color: "var(--text-primary)",
          border: "1px solid var(--border-secondary)",
          padding: "20px",
          borderRadius: "4px",
        }}
      >
        {/* Pond Name */}
        {currentData.pond_name && (
          <div
            style={{
              marginBottom: "25px",
              paddingBottom: "15px",
              borderBottom: "2px solid var(--border-secondary)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <h3
              style={{
                margin: 0,
                fontSize: "18px",
                color: "var(--text-primary)",
              }}
            >
              {currentData.pond_name}
            </h3>
            <button
              onClick={() => onEdit("pond_name")}
              style={{
                padding: "6px 20px",
                fontSize: "14px",
                border: "1px solid var(--border-primary)",
                backgroundColor: "var(--surface-secondary)",
                color: "var(--text-primary)",
                cursor: "pointer",
              }}
            >
              Edit
            </button>
          </div>
        )}

        {/* Yes/No Questions */}
        <div style={{ marginBottom: "25px" }}>
          {renderYesNoAnswer(
            "Is there a pond profile?",
            currentData.has_pond_profile,
            "has_pond_profile",
          )}
          {currentData.has_pond_profile &&
            renderYesNoAnswer(
              "Is the slope different on the left and right sides?",
              currentData.has_different_left_right_slopes,
              "has_different_slopes",
            )}
          {renderYesNoAnswer(
            "Is there a pond table?",
            currentData.has_pond_table,
            "has_pond_table",
          )}
          {renderYesNoAnswer(
            "Is there a pond detail?",
            currentData.has_pond_detail,
            "has_pond_detail",
          )}
          {renderYesNoAnswer(
            "Is there a dedicated pond plan view?",
            currentData.has_dedicated_plan_view,
            "has_dedicated_plan_view",
          )}
          {renderYesNoAnswer(
            "Is pond bottom a flat elevation?",
            currentData.is_pond_bottom_flat,
            "has_flat_pond_bottom",
          )}
          {renderYesNoAnswer(
            "Is there pond information on the grading plan view?",
            currentData.has_grading_plan_info,
            "has_grading_plan_info",
          )}
        </div>

        {/* Pond Profile Data */}
        {currentData.has_pond_profile &&
          renderDataSection(
            "Pond Profile Details",
            {
              profile_bottom_elevation: currentData.profile_bottom_elevation,
              profile_top_berm_elevation:
                currentData.profile_top_berm_elevation,
              profile_top_dam_elevation: currentData.profile_top_dam_elevation,
              profile_dam_width: currentData.profile_dam_width,
              profile_spillway_elevation:
                currentData.profile_spillway_elevation,
              profile_images: currentData.profile_images,
              // Slope fields - conditionally include based on has_different_left_right_slopes
              ...(currentData.has_different_left_right_slopes === true
                ? {
                    profile_slopes_detail_name:
                      currentData.profile_slopes_detail_name,
                    profile_slopes_detail_images:
                      currentData.profile_slopes_detail_images,
                    profile_slope_left: currentData.profile_slope_left,
                    profile_slope_right: currentData.profile_slope_right,
                  }
                : {
                    profile_slope_percentage:
                      currentData.profile_slope_percentage,
                  }),
            },
            "pond_profile_form",
          )}

        {/* Pond Table Data */}
        {currentData.has_pond_table &&
          renderDataSection(
            "Pond Table Details",
            {
              table_bottom_elevation: currentData.table_bottom_elevation,
              table_top_berm_elevation: currentData.table_top_berm_elevation,
              table_top_dam_elevation: currentData.table_top_dam_elevation,
              table_dam_width: currentData.table_dam_width,
              table_spillway_elevation: currentData.table_spillway_elevation,
              table_images: currentData.table_images,
            },
            "pond_table_form",
          )}

        {/* Pond Detail Data */}
        {currentData.has_pond_detail &&
          renderDataSection(
            "Pond Detail Details",
            {
              detail_bottom_elevation: currentData.detail_bottom_elevation,
              detail_top_berm_elevation: currentData.detail_top_berm_elevation,
              detail_top_dam_elevation: currentData.detail_top_dam_elevation,
              detail_dam_width: currentData.detail_dam_width,
              detail_spillway_elevation: currentData.detail_spillway_elevation,
              detail_images: currentData.detail_images,
            },
            "pond_detail_form",
          )}

        {/* Plan View Data */}
        {currentData.has_dedicated_plan_view &&
          renderDataSection(
            "Dedicated Plan View Details",
            {
              plan_view_bottom_elevation:
                currentData.plan_view_bottom_elevation,
              plan_view_top_berm_elevation:
                currentData.plan_view_top_berm_elevation,
              plan_view_top_dam_elevation:
                currentData.plan_view_top_dam_elevation,
              plan_view_dam_width: currentData.plan_view_dam_width,
              plan_view_spillway_elevation:
                currentData.plan_view_spillway_elevation,
              plan_view_images: currentData.plan_view_images,
            },
            "plan_view_form",
          )}

        {/* Flat Pond Bottom Data */}
        {currentData.is_pond_bottom_flat &&
          renderDataSection(
            "Pond Bottom Details",
            {
              pond_bottom_flat_images: currentData.pond_bottom_flat_images,
            },
            "pond_bottom_flat_form",
          )}

        {/* Grading Plan View Data */}
        {currentData.has_grading_plan_info &&
          renderDataSection(
            "Grading Plan View Details",
            {
              grading_plan_bottom_callout:
                currentData.grading_plan_bottom_callout,
              grading_plan_top_berm_elevation:
                currentData.grading_plan_top_berm_elevation,
              grading_plan_top_dam_elevation:
                currentData.grading_plan_top_dam_elevation,
              grading_plan_dam_width: currentData.grading_plan_dam_width,
              grading_plan_spillway_elevation:
                currentData.grading_plan_spillway_elevation,
              grading_plan_lowest_contour:
                currentData.grading_plan_lowest_contour,
              grading_plan_images: currentData.grading_plan_images,
            },
            "grading_plan_view_form",
          )}
      </div>

      {/* Action Buttons */}
      <div
        style={{
          marginTop: "25px",
          display: "flex",
          gap: "15px",
          alignItems: "center",
        }}
      >
        {canGoBack && (
          <button
            onClick={onBack}
            style={{
              padding: "12px 40px",
              fontSize: "16px",
              border: "1px solid var(--border-primary)",
              backgroundColor: "var(--surface-tertiary)",
              color: "var(--text-primary)",
              cursor: "pointer",
            }}
          >
            ← Back
          </button>
        )}

        {/* Only show Confirm & Continue button if not yet completed */}
        {!isCompleted && (
          <button
            onClick={onConfirm}
            style={{
              padding: "var(--space-3) var(--space-8)",
              fontSize: "var(--text-base)",
              fontWeight: "var(--font-semibold)",
              border: "1px solid var(--cta-orange)",
              backgroundColor: "var(--cta-orange)",
              color: "white",
              cursor: "pointer",
              borderRadius: "var(--radius-md)",
              transition: "all var(--transition-fast)",
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "var(--cta-orange-hover)";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "var(--cta-orange)";
            }}
          >
            Confirm & Continue
          </button>
        )}
      </div>
    </div>
  );
};

export default PondConfirmation;
